import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  initialThinkingSteps,
  OnAgentThinking,
  patchAssistantThinking,
  patchAssistantThinkingMany,
  thinkingHintFromMessages,
} from '@/components/core';
import { parseContextMap } from '@/components/features/ChatContainer/utils/contextMap';
import {
  readLibraryDocuments,
  SEARCH_LIBRARY_TOOL,
} from '@/components/features/ChatContainer/utils/libraryDocuments';
import {
  appendProgressNote,
  clearProgressStatus,
  closeProgressNote,
  createProgressNotes,
  demoteReplyToNote,
  progressMessageFields,
} from '@/components/features/ChatContainer/utils/progressNotes';
import {
  getStreamCharsPerTick,
  waitForStreamPaint,
} from '@/components/features/ChatContainer/utils/streamPacing';
import { separateReplyAfterTool } from '@/components/features/ChatContainer/utils/streamReply';
import { compactToolCallOutput } from '@/components/features/ChatContainer/utils/toolOutput';
import { usageToolCallOutput } from '@/components/features/ChatContainer/utils/usageChip';
import {
  trimPromptedMessage,
  type PromptUse,
} from '@/components/features/Editor';
import { COMMON_AGENT_MESSAGES } from '@/constant';
import { AgentName } from '@/constant/agents';
import { useChatSessionHistory } from '@/hooks/useChatSessionHistory';
import { createChatMessage } from '@/lib/utils';
import {
  connieService,
  ConnieStreamError,
  ConnieStreamEvent,
} from '@/services/connie';
import { ConnieVoiceMessage } from '@/services/connieVoice';
import { SessionHistoryResponse } from '@/services/sessions';
import { useAgents } from '@/store/agents';
import { ChatType, useChats } from '@/store/chats';
import { useRecordPromptUse } from '@/store/prompts';
import {
  CHAT_CHANNEL_TYPE,
  CHAT_MESSAGE_RATING,
  CHAT_USER_ROLE,
  ChatMessage,
} from '@/types/chat';
import { parseLibraryReferences } from '@/utils/libraryReferences';

type ConnieStreamingChatType = typeof ChatType.connie | typeof ChatType.job;

type UseConnieChatArgs = {
  chatType?: ConnieStreamingChatType;
  sessionId?: string;
  userId?: number;
  isSessionsMode?: boolean;
  contactId?: string;
  currentJobReference?: string;
  onMessage?: (message: ChatMessage) => void;
  onThinking?: OnAgentThinking;
  /** The user opened another session or a new chat (not a turn's own id). */
  onSessionSwitch?: () => void;
};

const initialMessages = [
  createChatMessage({
    content: COMMON_AGENT_MESSAGES.greeting,
    type: CHAT_CHANNEL_TYPE.BOT,
  }),
];

const mapSessionHistoryMessages = (
  messages: SessionHistoryResponse['messages']
): ChatMessage[] => {
  const rating = (msg: { is_positive: boolean; is_negative: boolean }) => {
    if (msg.is_positive) return CHAT_MESSAGE_RATING.POSITIVE;
    if (msg.is_negative) return CHAT_MESSAGE_RATING.NEGATIVE;
    return null;
  };

  return messages
    .sort((a, b) => a.created_at - b.created_at)
    .map(msg => ({
      ...createChatMessage({
        id: msg.id,
        rating: rating(msg),
        type:
          msg.role === CHAT_USER_ROLE.USER
            ? CHAT_CHANNEL_TYPE.HUMAN
            : CHAT_CHANNEL_TYPE.BOT,
        content: msg.body,
        timestamp: msg.created_at,
        tools: msg.tools,
      }),
      turnIndex: msg.turnIndex,
      libraryReferences: msg.libraryReferences?.length
        ? msg.libraryReferences
        : undefined,
      prompts: msg.prompts?.length ? msg.prompts : undefined,
    }));
};

const getDisplayError = (error: unknown) => {
  if (error instanceof ConnieStreamError) {
    return error.message;
  }

  return 'Connie could not complete that response. Please try again.';
};

const WORKING_STATUS_DELAY_MS = 120;
const TOOL_ACTIVITY_MIN_VISIBLE_MS = 120;

const waitForToolActivityPaint = () =>
  new Promise<void>(resolve => {
    window.setTimeout(() => {
      requestAnimationFrame(() => resolve());
    }, TOOL_ACTIVITY_MIN_VISIBLE_MS);
  });

const formatMessageTimestamp = (date = new Date()) =>
  date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const getVoiceFileExtension = (mimeType: string) => {
  const baseType = mimeType.split(';')[0]?.trim().toLowerCase() ?? '';
  if (
    baseType === 'audio/mp4' ||
    baseType === 'audio/m4a' ||
    baseType === 'audio/x-m4a' ||
    baseType === 'audio/mp4a-latm'
  ) {
    return 'm4a';
  }
  return 'webm';
};

export const useConnieChat = ({
  chatType = ChatType.connie,
  sessionId: sessionIdProp,
  userId,
  isSessionsMode,
  contactId,
  currentJobReference,
  onMessage,
  onThinking,
  onSessionSwitch,
}: UseConnieChatArgs) => {
  const { isNewSession, sessionId: storedSessionId } = useChats(
    state => state.chatState[chatType]
  );
  const updateChatState = useChats(state => state.updateChatState);
  const { data: aiAgent } = useAgents(AgentName.CONNIE);

  const resolvedSessionId = storedSessionId ?? sessionIdProp;
  const { data: sessionHistory } = useChatSessionHistory(
    chatType,
    resolvedSessionId ?? undefined,
    !isSessionsMode
  );
  const streamChannel =
    chatType === ChatType.job ? ('job' as const) : ('global' as const);

  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef(resolvedSessionId);
  const onSessionSwitchRef = useRef(onSessionSwitch);
  const voiceGenerationRef = useRef(0);
  onSessionSwitchRef.current = onSessionSwitch;
  // Avoid replacing a live/streamed thread with a lagging history fetch.
  const appliedHistorySessionRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!isLoading) {
      onThinking?.(false);
      return;
    }

    onThinking?.(true, thinkingHintFromMessages(messages));
  }, [isLoading, messages, onThinking]);

  useEffect(() => {
    const sessionChanged = sessionIdRef.current !== resolvedSessionId;

    if (sessionChanged) {
      controllerRef.current?.abort();
      controllerRef.current = null;
      setIsLoading(false);
      voiceGenerationRef.current += 1;
      onSessionSwitchRef.current?.();
    }

    if (!resolvedSessionId) {
      appliedHistorySessionRef.current = null;
      sessionIdRef.current = resolvedSessionId;
      updateChatState(chatType, { isNewSession: true });
      setMessages(initialMessages);
      return;
    }

    sessionIdRef.current = resolvedSessionId;

    if (isNewSession) {
      return;
    }

    if (appliedHistorySessionRef.current === resolvedSessionId) {
      return;
    }

    setMessages([
      createChatMessage({
        content: 'Loading...',
        type: CHAT_CHANNEL_TYPE.BOT,
        isLoader: true,
      }),
    ]);
  }, [chatType, resolvedSessionId, isNewSession, updateChatState]);

  useEffect(() => {
    if (!sessionHistory || isNewSession || isLoading) return;
    if (appliedHistorySessionRef.current === sessionHistory.session_id) return;

    appliedHistorySessionRef.current = sessionHistory.session_id;
    setMessages(mapSessionHistoryMessages(sessionHistory.messages));
  }, [sessionHistory, isNewSession, isLoading]);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    []
  );

  // One assistant turn's event handling, shared by the SSE stream and the
  // hands-free voice socket (which relays the same event types).
  const createTurnHandler = useCallback(
    (assistantMessage: ChatMessage, deferUserBubble: boolean) => {
      let workingStatusTimer: ReturnType<typeof setTimeout> | null = null;
      let progressNotes = createProgressNotes();
      let replyPausedForTool = false;

      const clearWorkingStatusTimer = () => {
        if (workingStatusTimer !== null) {
          clearTimeout(workingStatusTimer);
          workingStatusTimer = null;
        }
      };

      const scheduleWorkingStatus = () => {
        clearWorkingStatusTimer();
        workingStatusTimer = setTimeout(() => {
          workingStatusTimer = null;
          setMessages(previous =>
            previous.map(current =>
              current.id === assistantMessage.id && current.isStreaming
                ? patchAssistantThinking(
                    current,
                    { type: 'status', label: 'Working…' },
                    { streamStatus: 'Working…' }
                  )
                : current
            )
          );
        }, WORKING_STATUS_DELAY_MS);
      };

      const handle = async (event: ConnieStreamEvent) => {
        switch (event.type) {
          case 'transcription': {
            if (!deferUserBubble) return;

            const transcript = event.data.transcript;
            if (!transcript) return;

            const userMessage = createChatMessage({
              content: transcript,
              type: CHAT_CHANNEL_TYPE.HUMAN,
            });

            setMessages(previous => {
              const assistantIndex = previous.findIndex(
                current => current.id === assistantMessage.id
              );
              if (assistantIndex === -1) {
                return [...previous, userMessage];
              }

              return [
                ...previous.slice(0, assistantIndex),
                userMessage,
                patchAssistantThinking(
                  previous[assistantIndex],
                  { type: 'status', label: 'Preparing context…' },
                  { streamStatus: 'Preparing context…' }
                ),
                ...previous.slice(assistantIndex + 1),
              ];
            });
            return;
          }
          case 'summary_context':
            clearWorkingStatusTimer();
            progressNotes = closeProgressNote(progressNotes);
            setMessages(previous =>
              previous.map(current =>
                current.id === assistantMessage.id
                  ? patchAssistantThinking(
                      current,
                      { type: 'status', label: 'Preparing context…' },
                      {
                        streamStatus: 'Preparing context…',
                        ...progressMessageFields(progressNotes),
                      }
                    )
                  : current
              )
            );
            return;
          case 'progress': {
            clearWorkingStatusTimer();
            progressNotes = appendProgressNote(progressNotes, event.data.delta);
            setMessages(previous =>
              previous.map(current =>
                current.id === assistantMessage.id
                  ? patchAssistantThinking(
                      current,
                      { type: 'complete_status' },
                      {
                        streamStatus: undefined,
                        ...progressMessageFields(progressNotes),
                      }
                    )
                  : current
              )
            );
            return;
          }
          case 'text_delta': {
            clearWorkingStatusTimer();
            progressNotes = clearProgressStatus(progressNotes);
            const progressFields = progressMessageFields(progressNotes);
            let text = event.data.delta ?? event.data.text ?? '';
            if (!text) {
              setMessages(previous =>
                previous.map(current =>
                  current.id === assistantMessage.id
                    ? { ...current, ...progressFields }
                    : current
                )
              );
              return;
            }
            if (replyPausedForTool) {
              replyPausedForTool = false;
              if (assistantMessage.content.trim()) {
                text = separateReplyAfterTool(assistantMessage.content, text);
              }
            }

            const charsPerTick = getStreamCharsPerTick(text.length);
            for (let index = 0; index < text.length; index += charsPerTick) {
              assistantMessage.content += text.slice(
                index,
                index + charsPerTick
              );
              setMessages(previous =>
                previous.map(current =>
                  current.id === assistantMessage.id
                    ? patchAssistantThinking(
                        current,
                        { type: 'complete_status' },
                        {
                          content: assistantMessage.content,
                          streamStatus: undefined,
                          ...progressFields,
                        }
                      )
                    : current
                )
              );
              await waitForStreamPaint();
            }
            scheduleWorkingStatus();
            return;
          }
          case 'tool_start':
            clearWorkingStatusTimer();
            // Text streamed before a tool call is narration, not the reply.
            // It becomes a step note; the body restarts after the tool.
            progressNotes = demoteReplyToNote(
              progressNotes,
              assistantMessage.content
            );
            assistantMessage.content = '';
            setMessages(previous =>
              previous.map(current =>
                current.id === assistantMessage.id
                  ? patchAssistantThinking(
                      current,
                      {
                        type: 'tool_start',
                        toolName: event.data.toolName,
                        toolUseId: event.data.toolUseId,
                      },
                      {
                        content: '',
                        toolActivity: event.data,
                        streamStatus: undefined,
                        ...progressMessageFields(progressNotes),
                      }
                    )
                  : current
              )
            );
            // A tool result can follow in the same network frame. Yield
            // long enough for React to paint the active tool chip first.
            await waitForToolActivityPaint();
            return;
          case 'tool_result':
            progressNotes = closeProgressNote(progressNotes);
            replyPausedForTool = true;
            setMessages(previous =>
              previous.map(current =>
                current.id === assistantMessage.id
                  ? patchAssistantThinkingMany(
                      current,
                      progressNotes.status
                        ? [
                            {
                              type: 'tool_result',
                              toolUseId: event.data.toolUseId,
                            },
                          ]
                        : [
                            {
                              type: 'tool_result',
                              toolUseId: event.data.toolUseId,
                            },
                            { type: 'status', label: 'Working…' },
                          ],
                      {
                        toolActivity: undefined,
                        streamStatus: progressNotes.status
                          ? undefined
                          : 'Working…',
                        ...progressMessageFields(progressNotes),
                        toolCallOutputs: [
                          ...(current.toolCallOutputs ?? []),
                          compactToolCallOutput(event.data),
                        ],
                        // The working panel shows each Library search where it
                        // happened, with the documents it found.
                        librarySearches:
                          event.data.toolName === SEARCH_LIBRARY_TOOL
                            ? [
                                ...(current.librarySearches ?? []),
                                {
                                  afterNotes: progressNotes.notes.length,
                                  documents: readLibraryDocuments(
                                    event.data.toolName,
                                    event.data.output
                                  ).map(ref => ({
                                    documentId: ref.documentId,
                                    name: ref.name ?? '',
                                  })),
                                },
                              ]
                            : current.librarySearches,
                      }
                    )
                  : current
              )
            );
            return;
          case 'done': {
            clearWorkingStatusTimer();
            progressNotes = clearProgressStatus(progressNotes);
            sessionIdRef.current = event.data.sessionId;
            appliedHistorySessionRef.current = event.data.sessionId;
            updateChatState(chatType, {
              sessionId: event.data.sessionId,
              isNewSession: false,
            });

            let completedMessage: ChatMessage = assistantMessage;

            setMessages(previous =>
              previous.map(current => {
                if (current.id !== assistantMessage.id) {
                  return current;
                }

                const toolCallOutputs = (
                  event.data.toolCallOutputs ??
                  current.toolCallOutputs ??
                  []
                ).map(compactToolCallOutput);
                const usageOutput = usageToolCallOutput({
                  messageId: assistantMessage.id,
                  usage: event.data.usage,
                  sessionId: event.data.sessionId,
                  route: event.data.route,
                });

                completedMessage = patchAssistantThinking(
                  current,
                  { type: 'complete' },
                  {
                    content: assistantMessage.content,
                    isStreaming: false,
                    isLiveStream: false,
                    streamStatus: undefined,
                    toolActivity: undefined,
                    ...progressMessageFields(progressNotes),
                    timestamp: formatMessageTimestamp(),
                    tools: event.data.toolsUsed,
                    toolCallOutputs: usageOutput
                      ? [...toolCallOutputs, usageOutput]
                      : toolCallOutputs,
                    turnIndex:
                      typeof event.data.turnIndex === 'number'
                        ? event.data.turnIndex
                        : undefined,
                    libraryReferences: parseLibraryReferences(
                      event.data.libraryReferences
                    ),
                    contextMap: parseContextMap(event.data.contextMap),
                  }
                );
                return completedMessage;
              })
            );
            onMessage?.(completedMessage);
            return;
          }
          case 'error':
            progressNotes = clearProgressStatus(progressNotes);
            setMessages(previous =>
              previous.map(current =>
                current.id === assistantMessage.id
                  ? {
                      ...current,
                      ...progressMessageFields(progressNotes),
                    }
                  : current
              )
            );
            return;
          default: {
            const exhaustiveCheck: never = event;
            return exhaustiveCheck;
          }
        }
      };

      // Ends an unfinished turn: drops it when it was cancelled before any
      // content, otherwise closes it with what it has or with `errorText`.
      const finish = ({
        cancelled,
        errorText,
      }: {
        cancelled?: boolean;
        errorText?: string;
      }) => {
        clearWorkingStatusTimer();
        progressNotes = clearProgressStatus(progressNotes);
        const progressFields = progressMessageFields(progressNotes);
        setMessages(previous =>
          previous
            .filter(
              current =>
                // Drop empty in-flight assistant turns cancelled before content.
                !cancelled ||
                current.id !== assistantMessage.id ||
                Boolean(assistantMessage.content)
            )
            .map(current =>
              current.id === assistantMessage.id
                ? patchAssistantThinking(
                    current,
                    { type: 'complete' },
                    {
                      content: errorText ?? assistantMessage.content,
                      isStreaming: false,
                      isLiveStream: false,
                      streamStatus: undefined,
                      toolActivity: undefined,
                      ...progressFields,
                      ...(errorText && {
                        timestamp: formatMessageTimestamp(),
                      }),
                    }
                  )
                : current
            )
        );
      };

      return { handle, finish, dispose: clearWorkingStatusTimer };
    },
    [chatType, onMessage, updateChatState]
  );

  const runStream = useCallback(
    async ({
      request,
      assistantMessage,
      deferUserBubble,
    }: {
      request: Parameters<typeof connieService.stream>[0];
      assistantMessage: ChatMessage;
      deferUserBubble: boolean;
    }) => {
      const controller = new AbortController();
      controllerRef.current = controller;
      const turn = createTurnHandler(assistantMessage, deferUserBubble);

      setIsLoading(true);

      try {
        await connieService.stream(request, turn.handle, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) {
          turn.finish({ cancelled: true });
          return;
        }

        if (error instanceof ConnieStreamError && error.status === 403) {
          turn.dispose();
          sessionIdRef.current = '';
          appliedHistorySessionRef.current = null;
          updateChatState(chatType, {
            sessionId: '',
            isNewSession: true,
          });
          setMessages(initialMessages);
          return;
        }

        turn.finish({ errorText: getDisplayError(error) });
      } finally {
        turn.dispose();
        setIsLoading(false);
        if (controllerRef.current === controller) {
          controllerRef.current = null;
        }
      }
    },
    [chatType, createTurnHandler, updateChatState]
  );

  const withStreamContext = useCallback(
    <T extends { sessionId?: string }>(request: T) => ({
      ...request,
      channel: streamChannel,
      ...(contactId && { contactId }),
      ...(currentJobReference && { currentJobReference }),
    }),
    [contactId, currentJobReference, streamChannel]
  );

  const recordPromptUse = useRecordPromptUse();
  const sendMessage = useCallback(
    async (message: string, prompts?: PromptUse[]) => {
      if (isLoading) return;

      const { text: content, prompts: record } = trimPromptedMessage(
        message,
        prompts
      );
      if (!content || [...content].length > 5000) {
        return;
      }

      const activeSessionId = sessionIdRef.current ?? resolvedSessionId;
      const userMessage: ChatMessage = {
        ...createChatMessage({
          content,
          type: CHAT_CHANNEL_TYPE.HUMAN,
        }),
        ...(record.length > 0 && { prompts: record }),
      };
      const assistantMessage: ChatMessage = {
        ...createChatMessage({
          content: '',
          type: CHAT_CHANNEL_TYPE.BOT,
          isStreaming: true,
        }),
        isLiveStream: true,
        streamStatus: 'Working…',
        thinkingSteps: initialThinkingSteps('Working…'),
      };

      setIsLoading(true);
      setMessages(previous => [...previous, userMessage, assistantMessage]);

      await runStream({
        request: withStreamContext({
          message: content,
          sessionId: activeSessionId ?? undefined,
          ...(record.length > 0 && { prompts: record }),
        }),
        assistantMessage,
        deferUserBubble: false,
      });
      // Xano wrote Prompt Use rows: Recent and the usage counts refetch.
      if (record.length > 0) void recordPromptUse();
    },
    [
      isLoading,
      recordPromptUse,
      resolvedSessionId,
      runStream,
      withStreamContext,
    ]
  );

  const sendVoiceNote = useCallback(
    async (file: Blob) => {
      if (isLoading) return;
      if (!file || file.size === 0) return;

      const activeSessionId = sessionIdRef.current ?? resolvedSessionId;
      const extension = getVoiceFileExtension(file.type);
      const fileName = `${activeSessionId || 'new'}-${userId ?? 'user'}.${extension}`;
      const assistantMessage: ChatMessage = {
        ...createChatMessage({
          content: '',
          type: CHAT_CHANNEL_TYPE.BOT,
          isStreaming: true,
        }),
        isLiveStream: true,
        streamStatus: 'Transcribing…',
        thinkingSteps: initialThinkingSteps('Transcribing…'),
      };

      setIsLoading(true);
      setMessages(previous => [...previous, assistantMessage]);

      await runStream({
        request: withStreamContext({
          file,
          fileName,
          sessionId: activeSessionId ?? undefined,
        }),
        assistantMessage,
        deferUserBubble: true,
      });
    },
    [isLoading, resolvedSessionId, runStream, userId, withStreamContext]
  );

  // ── Hands-free voice (FSI voice socket) ──────────────────────────────────
  // Socket messages arrive faster than the turn handler paints text, and the
  // next turn can start while the last answer is still painting. Every voice
  // step, across turns, runs through one promise queue, so answers never
  // stream over each other.
  type VoiceTurn = {
    assistantMessage: ChatMessage;
    turn: ReturnType<typeof createTurnHandler>;
    streamed: boolean;
    closing: boolean;
  };
  const voiceTurnRef = useRef<VoiceTurn | null>(null);
  const voiceQueueRef = useRef<Promise<void>>(Promise.resolve());

  const enqueueVoiceStep = useCallback((step: () => unknown) => {
    const generation = voiceGenerationRef.current;
    voiceQueueRef.current = voiceQueueRef.current
      // Steps queued before a session switch must not paint the new thread.
      .then(() => (generation === voiceGenerationRef.current ? step() : null))
      .then(() => undefined)
      .catch(() => undefined);
  }, []);

  const closeVoiceTurn = useCallback(
    (step: (voiceTurn: VoiceTurn) => unknown) => {
      const voiceTurn = voiceTurnRef.current;
      if (!voiceTurn || voiceTurn.closing) return;
      voiceTurn.closing = true;
      enqueueVoiceStep(async () => {
        await step(voiceTurn);
        voiceTurn.turn.dispose();
        if (voiceTurnRef.current === voiceTurn) {
          voiceTurnRef.current = null;
          setIsLoading(false);
        }
      });
    },
    [enqueueVoiceStep]
  );

  const beginVoiceTurn = useCallback(
    (transcript: string) => {
      closeVoiceTurn(({ turn }) => turn.finish({ cancelled: true }));

      const userMessage = createChatMessage({
        content: transcript,
        type: CHAT_CHANNEL_TYPE.HUMAN,
      });
      const assistantMessage: ChatMessage = {
        ...createChatMessage({
          content: '',
          type: CHAT_CHANNEL_TYPE.BOT,
          isStreaming: true,
        }),
        isLiveStream: true,
        streamStatus: 'Working…',
        thinkingSteps: initialThinkingSteps('Working…'),
      };

      // Later socket events attach to this turn now; it shows once the
      // previous answer has finished painting.
      voiceTurnRef.current = {
        assistantMessage,
        turn: createTurnHandler(assistantMessage, false),
        streamed: false,
        closing: false,
      };
      enqueueVoiceStep(() => {
        setIsLoading(true);
        setMessages(previous => [...previous, userMessage, assistantMessage]);
      });
    },
    [closeVoiceTurn, createTurnHandler, enqueueVoiceStep]
  );

  /** Context for the voice socket's config message. */
  const getVoiceContext = useCallback(
    () => ({
      // The open chat session, so voice and typed turns share one thread.
      // Without one, FSI starts a session and every later turn on the
      // socket stays in it.
      ...((sessionIdRef.current || resolvedSessionId) && {
        sessionId: sessionIdRef.current || resolvedSessionId,
      }),
      ...(contactId && { contactId }),
      ...(currentJobReference && { currentJobReference }),
    }),
    [contactId, currentJobReference, resolvedSessionId]
  );

  /** Renders one voice socket message; true when it landed in the chat. */
  const handleVoiceMessage = useCallback(
    (message: ConnieVoiceMessage): boolean => {
      switch (message.type) {
        case 'end_of_turn':
          if (message.transcript) beginVoiceTurn(message.transcript);
          return true;
        case 'processing':
          // Push-to-talk has no end_of_turn; its turn starts here.
          if (message.transcript && !voiceTurnRef.current) {
            beginVoiceTurn(message.transcript);
          }
          return true;
        case 'stream': {
          const voiceTurn = voiceTurnRef.current;
          if (!voiceTurn || voiceTurn.closing) return false;
          const { event } = message;
          // Tia's document metadata; Connie never gets it.
          if (event.type === 'document_context') return true;
          voiceTurn.streamed = true;
          enqueueVoiceStep(() => voiceTurn.turn.handle(event));
          return true;
        }
        case 'agent_response': {
          const { data } = message;
          closeVoiceTurn(async ({ assistantMessage, turn, streamed }) => {
            // Guardrail and simple-route answers arrive whole, not streamed.
            if (!streamed && data.response) {
              assistantMessage.content = data.response;
            }
            await turn.handle({
              type: 'done',
              data: {
                sessionId: data.sessionId,
                toolsUsed: data.toolsUsed,
                toolCallOutputs: data.toolCallOutputs,
                usage: data.usage,
                route: data.route ?? undefined,
                turnIndex: data.turnIndex,
                libraryReferences: data.libraryReferences,
                contextMap: data.contextMap,
              },
            });
          });
          return true;
        }
        case 'error': {
          const voiceTurn = voiceTurnRef.current;
          if (!voiceTurn || voiceTurn.closing) return false;
          closeVoiceTurn(({ turn }) =>
            turn.finish({ errorText: message.message })
          );
          return true;
        }
        case 'cancelled':
        case 'listening':
          closeVoiceTurn(({ turn }) => turn.finish({ cancelled: true }));
          return true;
        default:
          return false;
      }
    },
    [beginVoiceTurn, closeVoiceTurn, enqueueVoiceStep]
  );

  const endVoiceCall = useCallback(() => {
    closeVoiceTurn(({ turn }) => turn.finish({ cancelled: true }));
  }, [closeVoiceTurn]);

  const abort = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  const updateMessageRating = useCallback(
    (messageId: string, rating: CHAT_MESSAGE_RATING) => {
      setMessages(previous =>
        previous.map(message =>
          message.id === messageId ? { ...message, rating } : message
        )
      );
    },
    []
  );

  return {
    aiAgent,
    isLoading,
    messages,
    sendMessage,
    sendVoiceNote,
    getVoiceContext,
    handleVoiceMessage,
    endVoiceCall,
    abort,
    updateMessageRating,
    sessionId: resolvedSessionId,
  };
};
